import React, { useState } from 'react';
import classNames from 'classnames';

import css from '../SearchPage.module.css';

/**
 * Renders the SEO accordion section at the bottom of a category search page.
 * Driven entirely by a seoConfig data object — no category-specific logic here.
 *
 * @param {Object} props
 * @param {Object} props.seoConfig - Category SEO config from seo/index.js
 */
const SeoBottomSection = ({ seoConfig }) => {
  const [openSection, setOpenSection] = useState(null);
  const toggle = id => setOpenSection(prev => (prev === id ? null : id));

  const { bottomSections, faq } = seoConfig;

  // Separate the leading prose (no accordion) from collapsible sections
  const leadingProse = bottomSections.find(s => s.type === 'prose');
  const accordionSections = bottomSections.filter(s => s.type !== 'prose');

  return (
    <section className={css.seoSection}>
      <div className={css.seoContent}>
        {/* Leading prose (always visible) */}
        {leadingProse && (
          <>
            <h2 className={css.seoH2}>{leadingProse.heading}</h2>
            {leadingProse.paragraphs?.map((p, i) => (
              <p key={i} className={css.seoParagraph} dangerouslySetInnerHTML={{ __html: p }} />
            ))}
            {leadingProse.list && (
              <ul className={css.seoList}>
                {leadingProse.list.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            )}
            {leadingProse.paragraphsAfter?.map((p, i) => (
              <p key={i} className={css.seoParagraph} dangerouslySetInnerHTML={{ __html: p }} />
            ))}
          </>
        )}

        <div className={css.seoAccordion}>
          {/* Collapsible content sections */}
          {accordionSections.map(section => (
            <div
              key={section.id}
              className={classNames(css.seoAccordionItem, {
                [css.seoAccordionItemOpen]: openSection === section.id,
              })}
            >
              <button
                className={css.seoAccordionHeader}
                onClick={() => toggle(section.id)}
                aria-expanded={openSection === section.id}
              >
                <h2 className={css.seoAccordionTitle}>{section.title}</h2>
                <span className={css.seoAccordionIndicator} aria-hidden="true" />
              </button>
              <div className={css.seoAccordionContent}>
                <div className={css.seoAccordionContentInner}>
                  {/* Regular paragraphs */}
                  {section.paragraphs?.map((p, i) => (
                    <p key={i} className={css.seoParagraph} dangerouslySetInnerHTML={{ __html: p }} />
                  ))}

                  {/* Optional subheadings (e.g. "Judaica Canvas Art", "Outdoor Mezuzah Cases") */}
                  {section.subheadings?.map((sub, i) => (
                    <React.Fragment key={i}>
                      <h3 className={css.seoH3}>{sub.title}</h3>
                      {sub.paragraphs?.map((p, j) => (
                        <p key={j} className={css.seoParagraph}>{p}</p>
                      ))}
                      {sub.list && (
                        <ul className={css.seoList}>
                          {sub.list.map((item, j) => <li key={j}>{item}</li>)}
                        </ul>
                      )}
                    </React.Fragment>
                  ))}

                  {/* Optional bullet list */}
                  {section.list && (
                    <ul className={css.seoList}>
                      {section.list.map((item, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  )}

                  {/* Optional paragraphs after list/subheadings */}
                  {section.paragraphsAfter?.map((p, i) => (
                    <p key={i} className={css.seoParagraph} dangerouslySetInnerHTML={{ __html: p }} />
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* FAQ accordion (always last) */}
          {faq && faq.length > 0 && (
            <div
              className={classNames(css.seoAccordionItem, {
                [css.seoAccordionItemOpen]: openSection === 'faq',
              })}
            >
              <button
                className={css.seoAccordionHeader}
                onClick={() => toggle('faq')}
                aria-expanded={openSection === 'faq'}
              >
                <h2 className={css.seoAccordionTitle}>{seoConfig.faqHeading || 'FAQ'}</h2>
                <span className={css.seoAccordionIndicator} aria-hidden="true" />
              </button>
              <div className={css.seoAccordionContent}>
                <div className={css.seoAccordionContentInner}>
                  {faq.map((item, i) => (
                    <div key={i} className={css.seoFaqItem}>
                      <h4 className={css.seoH4}>{item.question}</h4>
                      {Array.isArray(item.answer) ? (
                        item.answer.map((block, j) =>
                          Array.isArray(block) ? (
                            <ul key={j} className={css.seoList}>
                              {block.map((li, k) => <li key={k}>{li}</li>)}
                            </ul>
                          ) : (
                            <p key={j} className={css.seoParagraph} dangerouslySetInnerHTML={{ __html: block }} />
                          )
                        )
                      ) : (
                        <p className={css.seoParagraph}>{item.answer}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SeoBottomSection;
